/**
 * Shared client helpers for Andrei conversation management.
 *
 * Deletion goes through the backend (deleteAndreiConversationHttp) rather
 * than client-side batched deletes: legacy messages + old recap docs carry no
 * userUid (a rules-satisfiable client query can't even see them), and the
 * compaction archive collection is locked backend-only — so only the Admin
 * SDK can actually deliver "permanently removes the conversation".
 */

import { authFetch } from './cloudFunctionsAuth';

const DELETE_URL = 'https://us-central1-web-construct-app.cloudfunctions.net/deleteAndreiConversationHttp';

/** Parse a cloud-function Response tolerantly: edge timeouts/crash pages
 *  return non-JSON bodies, which must surface as readable errors rather
 *  than a SyntaxError from res.json(). */
export async function parseFnResponse(resp: Response): Promise<any> {
  let body: any = null;
  try { body = await resp.json(); } catch { /* non-JSON (edge error page) */ }
  if (!resp.ok || body?.ok === false) {
    throw new Error(body?.error || `HTTP ${resp.status}`);
  }
  return body || {};
}

export interface DeleteConversationResult {
  deletedMessages: number;
  deletedArchives: number;
  sessionDeleted: boolean;
}

/** Thoroughly delete one conversation (session doc + all its messages +
 *  its compaction archive parts) via the backend. Throws on failure. */
export async function deleteConversation(sessionId: string): Promise<DeleteConversationResult> {
  const resp = await authFetch(DELETE_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sessionId }),
  });
  const j = await parseFnResponse(resp);
  return {
    deletedMessages: Number(j.deletedMessages) || 0,
    deletedArchives: Number(j.deletedArchives) || 0,
    sessionDeleted: !!j.sessionDeleted,
  };
}
